import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as authApi from '@/api/endpoints/auth';
import { setUnauthorizedHandler } from '@/api/client';
import type { User } from '@/types/api';
import { AuthContext, type AuthContextValue, type AuthStatus, type SignInResult } from './context';
import { EXPIRY_WARNING_MS, clearToken, isExpired, msUntilExpiry, readToken, writeToken } from './token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUserState] = useState<User | null>(null);
  const [expiringSoon, setExpiringSoon] = useState(false);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearWarningTimer = useCallback(() => {
    if (warningTimer.current !== null) {
      clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
  }, []);

  const signOut = useCallback(() => {
    clearWarningTimer();
    clearToken();
    setUserState(null);
    setExpiringSoon(false);
    setStatus('anonymous');
    // Cached responses belong to the user who just left.
    queryClient.clear();
  }, [clearWarningTimer, queryClient]);

  /**
   * There is no refresh endpoint, so instead of silently dying mid-task the app
   * warns two minutes out and offers an inline re-login that keeps the page intact.
   */
  const scheduleExpiryWarning = useCallback(
    (token: string) => {
      clearWarningTimer();
      setExpiringSoon(false);

      const remaining = msUntilExpiry(token);
      if (remaining === null) return;

      const untilWarning = remaining - EXPIRY_WARNING_MS;
      if (untilWarning <= 0) {
        setExpiringSoon(true);
        return;
      }

      warningTimer.current = setTimeout(() => setExpiringSoon(true), untilWarning);
    },
    [clearWarningTimer]
  );

  const adopt = useCallback(
    async (token: string): Promise<User> => {
      writeToken(token);
      const me = await authApi.fetchCurrentUser();
      setUserState(me);
      setStatus('authenticated');
      scheduleExpiryWarning(token);
      return me;
    },
    [scheduleExpiryWarning]
  );

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      const result = await authApi.login(email, password);

      // An administrator's password buys a challenge, not a session. Nothing is
      // written to storage here — there is no token yet to write.
      if (result.mfa_required && result.challenge_token) {
        return {
          kind: 'mfa',
          challengeToken: result.challenge_token,
          enrollmentRequired: result.enrollment_required,
        };
      }
      if (!result.access_token) {
        throw new Error('Sign in did not return a session.');
      }
      return { kind: 'session', user: await adopt(result.access_token) };
    },
    [adopt]
  );

  const refreshSession = useCallback(
    async (email: string, password: string) => {
      const result = await authApi.login(email, password);
      if (!result.access_token) {
        // An admin re-authenticating still owes a code; the inline modal cannot
        // collect one, so send them through the full sign-in page.
        throw new Error('Sign in again to continue.');
      }
      const access_token = result.access_token;
      writeToken(access_token);
      scheduleExpiryWarning(access_token);
      // Anything fetched under the dead token may have failed; start clean.
      await queryClient.invalidateQueries();
    },
    [queryClient, scheduleExpiryWarning]
  );

  // A 401 from anywhere in the app means the session is genuinely gone.
  useEffect(() => {
    setUnauthorizedHandler(() => signOut());
    return () => setUnauthorizedHandler(null);
  }, [signOut]);

  // Restore the session on first load, before the shell renders.
  useEffect(() => {
    const token = readToken();
    if (!token || isExpired(token)) {
      clearToken();
      setStatus('anonymous');
      return;
    }

    let cancelled = false;
    authApi
      .fetchCurrentUser()
      .then((me) => {
        if (cancelled) return;
        setUserState(me);
        setStatus('authenticated');
        scheduleExpiryWarning(token);
      })
      .catch(() => {
        if (cancelled) return;
        clearToken();
        setStatus('anonymous');
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleExpiryWarning]);

  useEffect(() => clearWarningTimer, [clearWarningTimer]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      expiringSoon,
      signIn,
      adoptToken: adopt,
      signOut,
      refreshSession,
      setUser: setUserState,
    }),
    [status, user, expiringSoon, signIn, adopt, signOut, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
